import React, { useState, useEffect } from 'react';
import { useAuthContext, useIsAnkrAdmin } from '../../context/AuthContext';
import { adminApi } from '../../services/api';
import SidebarPanel from '../../components/admin/SidebarPanel';
import AgGridTable from '../../components/AgGridTable';

// Reusable form component for Core Server Credentials
const CoreServerCredentialForm = ({ newCredential, setNewCredential, handleCreateCredential, handleCloseSidebar }) => {
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (formError) {
      const timer = setTimeout(() => {
        setFormError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [formError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    
    try {
      await handleCreateCredential(e);
    } catch (err) {
      setFormError('Error creating credential: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title text-primary mb-0 border-0">Create New Core Server Credential</h5>
      </div>
      <div className="card-body">
        {formError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {formError}
            <button type="button" className="btn-close" onClick={() => setFormError('')}></button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="clientId" className="form-label">Client ID *</label>
            <input
              type="text"
              className="form-control"
              id="clientId"
              value={newCredential.client_id}
              onChange={(e) => setNewCredential({...newCredential, client_id: e.target.value})}
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="mb-3">
            <label htmlFor="description" className="form-label">Description</label>
            <input
              type="text"
              className="form-control"
              id="description"
              value={newCredential.description}
              onChange={(e) => setNewCredential({...newCredential, description: e.target.value})}
              placeholder="Optional description"
              disabled={isSubmitting}
            />
          </div>
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Creating...
                </>
              ) : (
                <>
                  <i className="fas fa-plus me-1"></i> Create Credential
                </>
              )}
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleCloseSidebar}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reusable form component for API Tokens
const ApiTokenForm = ({ newToken, setNewToken, handleGenerateToken, handleCloseSidebar }) => {
  const [formError, setFormError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-dismiss error after 5 seconds
  useEffect(() => {
    if (formError) {
      const timer = setTimeout(() => {
        setFormError('');
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [formError]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');
    setIsSubmitting(true);
    
    try {
      await handleGenerateToken(e);
    } catch (err) {
      setFormError('Error generating token: ' + (err.response?.data?.error || err.message));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="card">
      {/* <div className="card-header">
        <h5 className="card-title mb-0">Generate New API Token</h5>
      </div> */}
      <div className="card-body">
        {formError && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            {formError}
            <button type="button" className="btn-close" onClick={() => setFormError('')}></button>
          </div>
        )}
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Token Type *</label>
                <select
                  className="form-control"
                  value={newToken.api_token_type}
                  onChange={(e) => setNewToken({...newToken, api_token_type: e.target.value})}
                  required
                  disabled={isSubmitting}
                >
                  <option value="core_server_general">Core Server General</option>
                  <option value="core_server_admin">Core Server Admin</option>
                  <option value="core_server_patient_user">Core Server Patient User</option>
                  <option value="core_server_healthcare_user">Core Server Healthcare User</option>
                  <option value="core_server_healthcare_provider">Core Server Healthcare Provider</option>
                  <option value="core_server_ankr_user">Core Server Ankr User</option>
                </select>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Description</label>
                <input
                  type="text"
                  className="form-control"
                  value={newToken.description}
                  onChange={(e) => setNewToken({...newToken, description: e.target.value})}
                  placeholder="Optional description"
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Created By</label>
                <input
                  type="text"
                  className="form-control"
                  value={newToken.created_by}
                  onChange={(e) => setNewToken({...newToken, created_by: e.target.value})}
                  placeholder="admin_panel"
                  disabled={isSubmitting}
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label className="form-label">Expiration (seconds)</label>
                <input
                  type="number"
                  className="form-control"
                  value={newToken.expiration_seconds}
                  onChange={(e) => setNewToken({...newToken, expiration_seconds: parseInt(e.target.value)})}
                  placeholder="86400"
                  disabled={isSubmitting}
                />
                <small className="text-muted">Default: 86400 (24 hours)</small>
              </div>
            </div>
          </div>
          {/* Core Server User ID field - only show for user token types (exclude provider tokens) */}
          {['core_server_patient_user', 'core_server_healthcare_user', 'core_server_ankr_user'].includes(newToken.api_token_type) && (
            <div className="row">
              <div className="col-md-6">
                <div className="mb-3">
                  <label className="form-label">Core Server User ID *</label>
                  <input
                    type="text"
                    className="form-control"
                    value={newToken.core_server_user_id}
                    onChange={(e) => setNewToken({...newToken, core_server_user_id: e.target.value})}
                    placeholder="e.g., user@example.com"
                    required
                    disabled={isSubmitting}
                  />
                  <small className="text-muted">The Core Server user ID for whom this token is being generated</small>
                </div>
              </div>
            </div>
          )}
          {/* Show additional fields for all user token types */}
          {['core_server_patient_user', 'core_server_healthcare_user', 'core_server_healthcare_provider', 'core_server_ankr_user'].includes(newToken.api_token_type) && (
            <>
              {newToken.api_token_type === 'core_server_patient_user' && (
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <label className="form-label">Patient ID *</label>
                      <input
                        type="text"
                        className="form-control"
                        value={newToken.patient_id}
                        onChange={(e) => setNewToken({...newToken, patient_id: e.target.value})}
                        placeholder="e.g., PATIENT-12345"
                        required
                        disabled={isSubmitting}
                      />
                      <small className="text-muted">The Patient ID for whom this token is being generated</small>
                    </div>
                  </div>
                </div>
              )}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Health System ID {newToken.api_token_type === 'core_server_healthcare_user' && '*'}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={newToken.health_system_id || ''}
                      onChange={(e) => setNewToken({...newToken, health_system_id: e.target.value})}
                      placeholder={newToken.api_token_type === 'core_server_healthcare_user' ? "Required Health System ID" : "Optional Health System ID"}
                      required={newToken.api_token_type === 'core_server_healthcare_user'}
                      disabled={isSubmitting}
                    />
                    <small className="text-muted">
                      {newToken.api_token_type === 'core_server_healthcare_user' 
                        ? "Required for healthcare user tokens" 
                        : newToken.api_token_type === 'core_server_healthcare_provider'
                        ? "Optional for provider tokens (will auto-populate from provider if not provided)"
                        : "Associate token with a specific Health System"}
                    </small>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label">
                      Provider ID {newToken.api_token_type === 'core_server_healthcare_provider' && '*'}
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      value={newToken.provider_id || ''}
                      onChange={(e) => setNewToken({...newToken, provider_id: e.target.value})}
                      placeholder={newToken.api_token_type === 'core_server_healthcare_provider' ? "Required Provider ID" : "Optional Provider ID"}
                      required={newToken.api_token_type === 'core_server_healthcare_provider'}
                      disabled={isSubmitting}
                    />
                    <small className="text-muted">
                      {newToken.api_token_type === 'core_server_healthcare_provider' 
                        ? "Required for healthcare provider tokens" 
                        : "Associate token with a specific Provider"}
                    </small>
                  </div>
                </div>
              </div>
            </>
          )}
          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                  Generating...
                </>
              ) : (
                <>
                  <i className="fas fa-key me-1"></i> Generate Token
                </>
              )}
            </button>
            <button 
              type="button" 
              className="btn btn-secondary"
              onClick={handleCloseSidebar}
              disabled={isSubmitting}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Reusable component for displaying audit logs
const AuditLogsDisplay = ({ logs, title, formatDate }) => {
  return (
    <div className="card">
      <div className="card-header">
        <h5 className="card-title mb-0">{title}</h5>
      </div>
      <div className="card-body">
        <div className="table-responsive">
          <table className="table table-bordered table-striped">
            <thead>
              <tr>
                <th>Timestamp</th>
                <th>Action</th>
                <th>Result</th>
                <th>User</th>
                <th>Details</th>
              </tr>
            </thead>
            <tbody>
              {logs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center">No audit logs found</td>
                </tr>
              ) : (
                logs.slice(0, 10).map((log, index) => (
                  <tr key={index}>
                    <td>{formatDate(log.timestamp)}</td>
                    <td>{log.access_type}</td>
                    <td>
                      <span className={`badge ${log.success ? 'bg-success' : 'bg-danger'}`}>
                        {log.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                    <td>{log.user_id || 'N/A'}</td>
                    <td>{log.details || 'N/A'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};


const CoreServerCredentialsPage = () => {
  const { apiToken } = useAuthContext();
  const isAnkrAdmin = useIsAnkrAdmin();
  const [activeTab, setActiveTab] = useState(isAnkrAdmin ? 'credentials' : 'tokens');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSidebar, setShowSidebar] = useState(false);
  const [sidebarContent, setSidebarContent] = useState(null); // 'createCredential', 'editCredential', 'createToken', 'editToken', 'credentialLogs', 'tokenLogs'
  const [selectedCredential, setSelectedCredential] = useState(null);
  const [selectedToken, setSelectedToken] = useState(null);

  // Credentials Tab State
  const [credentials, setCredentials] = useState([]);
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [newCredential, setNewCredential] = useState({ client_id: '', description: '' });
  const [createdCredential, setCreatedCredential] = useState(null);
  const [credentialsAuditLogs, setCredentialsAuditLogs] = useState([]);

  // Tokens Tab State
  const [apiTokens, setApiTokens] = useState([]);
  const [filteredTokens, setFilteredTokens] = useState([]);
  const [newToken, setNewToken] = useState({
    api_token_type: 'core_server_general',
    description: '',
    created_by: 'admin_panel',
    expiration_seconds: 86400,
    core_server_user_id: '',
    patient_id: ''
  });
  const [createdToken, setCreatedToken] = useState(null);
  const [showTokenModal, setShowTokenModal] = useState(false);
  const [tokensAuditLogs, setTokensAuditLogs] = useState([]);

  // Filter State
  const [filters, setFilters] = useState({
    tokenType: '',
    status: '',
    createdBy: '',
    dateFrom: '',
    dateTo: ''
  });

  // Effect to add/remove 'open' class for sidebar animation
  useEffect(() => {
    const sidebar = document.querySelector('.sidebar-panel');
    if (sidebar) {
      if (showSidebar) { // Open sidebar for form
        sidebar.classList.add('open');
      } else {
        sidebar.classList.remove('open');
      }
    }
  }, [showSidebar]);
  // Close sidebar function
  const handleCloseSidebar = () => {
    setShowSidebar(false);
    setSidebarContent(null);
    setSelectedCredential(null);
    setSelectedToken(null);
  };

  // Fetch credentials
  const fetchCredentials = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getCoreServerCredentials();
      if (response.data) {
        setCredentials(response.data.credentials || []);
      } else {
        setCredentials([]);
      }
    } catch (err) {
      console.error('Error fetching credentials:', err);
      setError('Error fetching credentials: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Fetch API tokens
  const fetchApiTokens = async () => {
    try {
      setLoading(true);
      const response = await adminApi.getApiTokens();
      if (response.data) {
        const tokens = response.data.tokens || [];
        setApiTokens(tokens);
        setFilteredTokens(tokens);
      } else {
        setApiTokens([]);
        setFilteredTokens([]);
      }
    } catch (err) {
      console.error('Error fetching API tokens:', err);
      setError('Error fetching API tokens: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  // Filter tokens based on current filters
  const applyFilters = () => {
    let filtered = [...apiTokens];

    // Filter by token type
    if (filters.tokenType) {
      filtered = filtered.filter(token => token.api_token_type === filters.tokenType);
    }

    // Filter by status
    if (filters.status) {
      const isActive = filters.status === 'active';
      filtered = filtered.filter(token => token.is_active === isActive);
    }

    // Filter by created by
    if (filters.createdBy) {
      filtered = filtered.filter(token =>
        token.created_by.toLowerCase().includes(filters.createdBy.toLowerCase())
      );
    }

    // Filter by date range
    if (filters.dateFrom) {
      const fromDate = new Date(filters.dateFrom);
      filtered = filtered.filter(token => new Date(token.created_at) >= fromDate);
    }

    if (filters.dateTo) {
      const toDate = new Date(filters.dateTo);
      toDate.setHours(23, 59, 59, 999); // End of day
      filtered = filtered.filter(token => new Date(token.created_at) <= toDate);
    }

    setFilteredTokens(filtered);
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      tokenType: '',
      status: '',
      createdBy: '',
      dateFrom: '',
      dateTo: ''
    });
    setFilteredTokens(apiTokens);
  };

  // Apply filters whenever filters or apiTokens change
  useEffect(() => {
    applyFilters();
  }, [filters, apiTokens]);

  // Fetch audit logs
  const fetchAuditLogs = async (type = 'all') => {
    try {
      const filters = {};
      if (type === 'credentials') {
        filters.access_type = 'core_server_credential_create,core_server_credential_update,core_server_credential_delete';
      } else if (type === 'tokens') {
        filters.access_type = 'admin_system_api_token_generate,system_user_api_token_generate,user_api_token_generate,api_token_activate,api_token_deactivate,admin_api_tokens_list';
      }
      
      const response = await adminApi.getAuditLogs(filters);
      if (response.data) {
        if (type === 'credentials') {
          setCredentialsAuditLogs(response.data.data || []);
        } else if (type === 'tokens') {
          setTokensAuditLogs(response.data.data || []);
        }
      }
    } catch (err) {
      console.error('Error fetching audit logs:', err);
    }
  };

  // Create new credential
  const handleCreateCredential = async (e) => {
    e.preventDefault();
    const response = await adminApi.createCoreServerCredential(newCredential);
    
    if (response.data) {
      setCreatedCredential(response.data.credential);
      setShowSecretModal(true);
      handleCloseSidebar(); // Close sidebar after creation
      setNewCredential({ client_id: '', description: '' });
      fetchCredentials();
      fetchAuditLogs('credentials');
    }
  };

  // Toggle credential active status
  const toggleCredentialStatus = async (credentialId, isActive) => {
    try {
      await adminApi.updateCoreServerCredential(credentialId, { is_active: !isActive });
      fetchCredentials();
      fetchAuditLogs('credentials');
    } catch (err) {
      console.error('Error updating credential:', err);
      setError('Error updating credential: ' + (err.response?.data?.error || err.message));
    }
  };

  // Regenerate secret
  const regenerateSecret = async (credentialId) => {
    if (!window.confirm('Are you sure you want to regenerate the secret? This will invalidate the current secret.')) {
      return;
    }

    try {
      const response = await adminApi.updateCoreServerCredential(credentialId, { regenerate_secret: true });
      
      if (response.data && response.data.credential.new_secret) {
        setCreatedCredential({
          ...response.data.credential,
          secret: response.data.credential.new_secret
        });
        setShowSecretModal(true);
      }
      fetchCredentials();
      fetchAuditLogs('credentials');
    } catch (err) {
      console.error('Error regenerating secret:', err);
      setError('Error regenerating secret: ' + (err.response?.data?.error || err.message));
    }
  };

  // Delete credential
  const deleteCredential = async (credentialId, clientId) => {
    if (!window.confirm(`Are you sure you want to delete credential "${clientId}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await adminApi.deleteCoreServerCredential(credentialId);
      fetchCredentials();
      fetchAuditLogs('credentials');
    } catch (err) {
      console.error('Error deleting credential:', err);
      setError('Error deleting credential: ' + (err.response?.data?.error || err.message));
    }
  };

  // Generate API token
  const handleGenerateToken = async (e) => {
    e.preventDefault();

    // Use the unified admin token endpoint for all token types
    const tokenData = {
      api_token_type: newToken.api_token_type,
      description: newToken.description || `Admin-generated ${newToken.api_token_type} token`,
      created_by: newToken.created_by,
      expiration_seconds: newToken.expiration_seconds,
      core_server_user_id: newToken.core_server_user_id || null,
      patient_id: newToken.patient_id || null,
      health_system_id: newToken.health_system_id || null,
      provider_id: newToken.provider_id || null
    };

    const response = await adminApi.generateCoreServerApiToken(tokenData);

    if (response.data) {
      setCreatedToken(response.data);
      setShowTokenModal(true);
      handleCloseSidebar(); // Close sidebar after creation
      setNewToken({
        api_token_type: 'core_server_general',
        description: '',
        created_by: 'admin_panel',
        expiration_seconds: 86400,
        core_server_user_id: '',
        patient_id: ''
      });
      fetchApiTokens();
      fetchAuditLogs('tokens');
    }
  };

  // Toggle token active status
  const toggleTokenStatus = async (tokenId, isActive) => {
    try {
      if (isActive) {
        await adminApi.deactivateApiToken(tokenId);
      } else {
        await adminApi.activateApiToken(tokenId);
      }
      fetchApiTokens();
      fetchAuditLogs('tokens');
    } catch (err) {
      console.error('Error updating token:', err);
      setError('Error updating token: ' + (err.response?.data?.error || err.message));
    }
  };

  // Delete token
  const deleteToken = async (tokenId) => {
    if (!window.confirm('Are you sure you want to delete this token? This action cannot be undone.')) {
      return;
    }

    try {
      await adminApi.deleteApiToken(tokenId);
      fetchApiTokens();
      fetchAuditLogs('tokens');
    } catch (err) {
      console.error('Error deleting token:', err);
      setError('Error deleting token: ' + (err.response?.data?.error || err.message));
    }
  };

  useEffect(() => {
    if (activeTab === 'credentials') {
      fetchCredentials();
      fetchAuditLogs('credentials');
    } else if (activeTab === 'tokens') {
      fetchApiTokens();
      fetchAuditLogs('tokens');
    }
  }, [activeTab]);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <>
      {/* Page Title */}
      <div className="row">
        <div className="col-12">
          <div className="page-title-box d-sm-flex align-items-center justify-content-between">
            <h4 className="mb-sm-0 font-size-18">Credentials and Session Management</h4>
            <div className="page-title-right">
              <ol className="breadcrumb m-0">
                <li className="breadcrumb-item"><a href="/admin">Admin</a></li>
                <li className="breadcrumb-item active">Credentials Management</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="alert alert-danger alert-dismissible fade show" role="alert">
          {error}
          <button type="button" className="btn-close" onClick={() => setError('')}></button>
        </div>
      )}

      {/* Tabs */}
              {isAnkrAdmin && (<ul className="nav nav-tabs nav-tabs-custom nav-justified mt-3" role="tablist">
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'credentials' ? 'active' : ''}`}
                    onClick={() => setActiveTab('credentials')}
                    role="tab"
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="d-block d-sm-none"><i className="fas fa-key"></i></span>
                    <span className="d-none d-sm-block">Core Server Credentials</span>
                  </a>
                </li>
                <li className="nav-item">
                  <a 
                    className={`nav-link ${activeTab === 'tokens' ? 'active' : ''}`}
                    onClick={() => setActiveTab('tokens')}
                    role="tab"
                    style={{ cursor: 'pointer' }}
                  >
                    <span className="d-block d-sm-none"><i className="fas fa-coins"></i></span>
                    <span className="d-none d-sm-block">Access Tokens</span>
                  </a>
                </li>
              </ul>)}

              <div className="tab-content p-3 text-muted">
                {/* Credentials Tab */}
                {activeTab === 'credentials' && (
                  <div className="tab-pane active">
                    {/* Credentials Table */}
                    <div className="row mb-4">
                      <div className="col-12">
                        <div className="card">
                          <div className="card-header d-flex justify-content-between">
                            <p className="card-title text-lg mb-0">Core Server Credentials</p>
                            <button 
                              className="btn btn-primary btn-sm"
                              onClick={() => {
                                setShowSidebar(true);
                                setSidebarContent('createCredential');
                              }}
                            >
                              <i className="fas fa-plus me-1"></i> Create New Credential
                            </button>
                          </div>
                            {loading ? (
                              <div className="text-center">
                                <div className="spinner-border" role="status">
                                  <span className="visually-hidden">Loading...</span>
                                </div>
                              </div>
                            ) : credentials.length === 0 ? (
                              <div className="alert text-secondary text-center">No credentials found</div>
                            ) : (
                              <AgGridTable
                                rowData={credentials}
                                height="100%"
                                columnDefs={[
                                  {
                                    headerName: 'Client ID',
                                    field: 'client_id',
                                    width: 200,
                                    cellRenderer: (params) => params.data ? <code>{params.data.client_id}</code> : null
                                  },
                                  {
                                    headerName: 'Description',
                                    field: 'description',
                                    width: 200,
                                    valueGetter: (p) => p.data?.description || '-'
                                  },
                                  {
                                    headerName: 'Status',
                                    field: 'is_active',
                                    width: 100,
                                    valueGetter: (p) => p.data?.is_active ? 'Active' : 'Inactive',
                                    cellRenderer: (params) => {
                                      if (!params.data) return null;
                                      return (
                                        <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-danger'}`}>
                                          {params.data.is_active ? 'Active' : 'Inactive'}
                                        </span>
                                      );
                                    }
                                  },
                                  {
                                    headerName: 'Created',
                                    field: 'created_at',
                                    width: 160,
                                    valueGetter: (p) => formatDate(p.data?.created_at)
                                  },
                                  {
                                    headerName: 'Actions',
                                    field: 'actions',
                                    width: 150,
                                    sortable: false,
                                    filter: false,
                                    cellRenderer: (params) => {
                                      if (!params.data) return null;
                                      const cred = params.data;
                                      return (
                                        <div className="btn-group btn-group-sm" role="group">
                                          <button
                                            className={`btn ${cred.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                            onClick={() => toggleCredentialStatus(cred.id, cred.is_active)}
                                            title={cred.is_active ? 'Deactivate' : 'Activate'}
                                          >
                                            <i className={`fas ${cred.is_active ? 'fa-pause' : 'fa-play'}`}></i>
                                          </button>
                                          <button
                                            className="btn btn-outline-info"
                                            onClick={() => regenerateSecret(cred.id)}
                                            title="Regenerate Secret"
                                          >
                                            <i className="fas fa-sync"></i>
                                          </button>
                                          <button
                                            className="btn btn-outline-danger"
                                            onClick={() => deleteCredential(cred.id, cred.client_id)}
                                            title="Delete"
                                          >
                                            <i className="fas fa-trash"></i>
                                          </button>
                                        </div>
                                      );
                                    }
                                  }
                                ]}
                                defaultColDef={{
                                  flex: 1,
                                  minWidth: 100,
                                  resizable: true,
                                  sortable: true,
                                  filter: true,
                                }}
                                gridOptions={{
                                  domLayout: 'autoHeight',
                                  autoSizeStrategy: { type: 'fitGridWidth' }
                                }}
                                getRowId={(params) => `${params.data.id}`}
                              />
                            )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Tokens Tab */}
                {activeTab === 'tokens' && (
                  <div className="tab-pane active">
                    {/* Token Filters */}
                    <div className="card">
                      <div className="card-body">
                        <div className="row g-3">
                          <div className="col-md-3">
                            <label className="form-label">Token Type</label>
                            <select
                              className="form-select"
                              value={filters.tokenType}
                              onChange={(e) => setFilters({...filters, tokenType: e.target.value})}
                            >
                              <option value="">All Types</option>
                              <option value="core_server_general">Core Server General</option>
                              <option value="core_server_admin">Core Server Admin</option>
                              <option value="core_server_patient_user">Core Server Patient User</option>
                              <option value="core_server_healthcare_user">Core Server Healthcare User</option>
                              <option value="core_server_healthcare_provider">Core Server Healthcare Provider</option>
                              <option value="core_server_ankr_user">Core Server Ankr User</option>
                              <option value="external_2fa_client">External 2FA Client</option>
                              <option value="system_admin">System Admin</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Status</label>
                            <select
                              className="form-select"
                              value={filters.status}
                              onChange={(e) => setFilters({...filters, status: e.target.value})}
                            >
                              <option value="">All Status</option>
                              <option value="active">Active</option>
                              <option value="inactive">Inactive</option>
                            </select>
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Created By</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search creator..."
                              value={filters.createdBy}
                              onChange={(e) => setFilters({...filters, createdBy: e.target.value})}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Date From</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filters.dateFrom}
                              onChange={(e) => setFilters({...filters, dateFrom: e.target.value})}
                            />
                          </div>
                          <div className="col-md-2">
                            <label className="form-label">Date To</label>
                            <input
                              type="date"
                              className="form-control"
                              value={filters.dateTo}
                              onChange={(e) => setFilters({...filters, dateTo: e.target.value})}
                            />
                          </div>
                          <div className="col-md-1 d-flex align-items-end">
                            <button
                              type="button"
                              className="btn btn-outline-secondary"
                              onClick={clearFilters}
                              title="Clear all filters"
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          </div>
                        </div>
                        <div className="row mt-2">
                          <div className="col-12">
                            <small className="text-muted">
                              Showing {filteredTokens.length} of {apiTokens.length} tokens
                            </small>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Tokens Table */}
                    <div className="card">
                      <div className="card-header d-flex justify-content-between">
                        <p className="card-title text-lg mb-0">Access Tokens</p>
                        {isAnkrAdmin && (<button 
                          className="btn btn-primary btn-sm"
                          onClick={() => {
                            setShowSidebar(true);
                            setSidebarContent('createToken');
                          }}
                        >
                          <i className="fas fa-plus me-1"></i> Generate New Token
                        </button>)}
                      </div>
                        {loading ? (
                          <div className="text-center">
                            <div className="spinner-border" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                          </div>
                        ) : filteredTokens.length === 0 ? (
                          <div className="alert text-secondary text-center">No API tokens found</div>
                        ) : (
                          <AgGridTable
                            rowData={filteredTokens}
                            height="100%"
                            columnDefs={[
                              {
                                headerName: 'Token ID',
                                field: 'api_token_id',
                                width: 180,
                                cellRenderer: (params) => {
                                  if (!params.data) return null;
                                  return (
                                    <div>
                                      <code>{params.data.api_token_id.substring(0, 8)}...</code>
                                      <button 
                                        className="btn btn-sm btn-outline-secondary ms-2"
                                        onClick={() => copyToClipboard(params.data.api_token_id)}
                                        title="Copy to clipboard"
                                      >
                                        <i className="fas fa-copy"></i>
                                      </button>
                                    </div>
                                  );
                                }
                              },
                              {
                                headerName: 'Type',
                                field: 'api_token_type',
                                width: 180
                              },
                              {
                                headerName: 'Description',
                                field: 'description',
                                width: 150,
                                valueGetter: (p) => p.data?.description || '-'
                              },
                              {
                                headerName: 'Created By',
                                field: 'created_by',
                                width: 120
                              },
                              {
                                headerName: 'Created At',
                                field: 'created_at',
                                width: 150,
                                valueGetter: (p) => formatDate(p.data?.created_at)
                              },
                              {
                                headerName: 'Expires At',
                                field: 'expires_at',
                                width: 150,
                                valueGetter: (p) => p.data?.expires_at ? formatDate(p.data.expires_at) : 'Never'
                              },
                              {
                                headerName: 'Status',
                                field: 'is_active',
                                width: 100,
                                valueGetter: (p) => p.data?.is_active ? 'Active' : 'Inactive',
                                cellRenderer: (params) => {
                                  if (!params.data) return null;
                                  return (
                                    <span className={`badge ${params.data.is_active ? 'bg-success' : 'bg-danger'}`}>
                                      {params.data.is_active ? 'Active' : 'Inactive'}
                                    </span>
                                  );
                                }
                              },
                              {
                                headerName: 'Actions',
                                field: 'actions',
                                width: 120,
                                sortable: false,
                                filter: false,
                                cellRenderer: (params) => {
                                  if (!params.data) return null;
                                  const token = params.data;
                                  return (
                                    <div className="btn-group btn-group-sm" role="group">
                                      <button
                                        className={`btn ${token.is_active ? 'btn-outline-warning' : 'btn-outline-success'}`}
                                        onClick={() => toggleTokenStatus(token.api_token_id, token.is_active)}
                                        title={token.is_active ? 'Deactivate' : 'Activate'}
                                      >
                                        <i className={`fas ${token.is_active ? 'fa-pause' : 'fa-play'}`}></i>
                                      </button>
                                      <button
                                        className="btn btn-outline-danger"
                                        onClick={() => deleteToken(token.api_token_id)}
                                        title="Delete"
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </div>
                                  );
                                }
                              }
                            ]}
                            defaultColDef={{
                              flex: 1,
                              minWidth: 80,
                              resizable: true,
                              sortable: true,
                              filter: true,
                            }}
                            gridOptions={{
                              domLayout: 'autoHeight',
                              autoSizeStrategy: { type: 'fitGridWidth' }
                            }}
                            getRowId={(params) => `${params.data.api_token_id}`}
                          />
                        )}
                    </div>
                  </div>
                )}
      </div>

      {/* Secret Modal */}
      {showSecretModal && createdCredential && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Credential Created Successfully!</h5>
                <button type="button" className="btn-close" onClick={() => setShowSecretModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Please save the following secret. It will not be shown again:</p>
                <div className="alert alert-info">
                  <strong>Client ID:</strong> <code>{createdCredential.client_id}</code><br/>
                  <strong>Client Secret:</strong> <code>{createdCredential.secret}</code>
                  <button 
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={() => copyToClipboard(createdCredential.secret)}
                    title="Copy to clipboard"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowSecretModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Token Modal */}
      {showTokenModal && createdToken && (
        <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">API Token Generated Successfully!</h5>
                <button type="button" className="btn-close" onClick={() => setShowTokenModal(false)}></button>
              </div>
              <div className="modal-body">
                <p>Please save the following token. It will not be shown again:</p>
                <div className="alert alert-info">
                  <strong>Token ID:</strong> <code>{createdToken.api_token_id}</code><br/>
                  <strong>Token:</strong> <code>{createdToken.api_token}</code>
                  <button 
                    className="btn btn-sm btn-outline-secondary ms-2"
                    onClick={() => copyToClipboard(createdToken.api_token)}
                    title="Copy to clipboard"
                  >
                    <i className="fas fa-copy"></i>
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowTokenModal(false)}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sidebar */}
      <SidebarPanel
        isOpen={showSidebar}
        onClose={handleCloseSidebar}
        title={
          sidebarContent === 'createCredential' ? 'Create New Credential' :
          sidebarContent === 'createToken' ? 'Generate New API Token' :
          sidebarContent === 'credentialLogs' ? 'Credentials Audit Logs' :
          sidebarContent === 'tokenLogs' ? 'API Tokens Audit Logs' : ''
        }
      >
        {sidebarContent === 'createCredential' && (
          <CoreServerCredentialForm 
            newCredential={newCredential}
            setNewCredential={setNewCredential}
            handleCreateCredential={handleCreateCredential}
            handleCloseSidebar={handleCloseSidebar}
          />
        )}
        {sidebarContent === 'createToken' && (
          <ApiTokenForm
            newToken={newToken}
            setNewToken={setNewToken}
            handleGenerateToken={handleGenerateToken}
            handleCloseSidebar={handleCloseSidebar}
          />
        )}
        {sidebarContent === 'credentialLogs' && (
          <AuditLogsDisplay 
            logs={credentialsAuditLogs} 
            title="Credentials Audit Logs" 
            formatDate={formatDate} 
          />
        )}
        {sidebarContent === 'tokenLogs' && (
          <AuditLogsDisplay 
            logs={tokensAuditLogs} 
            title="API Tokens Audit Logs" 
            formatDate={formatDate} 
          />
        )}
      </SidebarPanel>
    </>
  );
};

export default CoreServerCredentialsPage;